import axios from 'axios';

// Always use current domain for API calls to avoid CORS issues in production
// This ensures the frontend talks to the same domain it's served from
const API_URL = window.location.origin;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true, // Send cookies with requests
});

// Request interceptor - no longer needs to manually attach token
// The httpOnly cookie is automatically sent with withCredentials: true
api.interceptors.request.use((config) => {
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login for protected routes
      // Don't redirect if we're already on a public page or during initial auth check
      const publicPaths = ['/', '/login', '/register', '/careers', '/apply', '/forgot-password', '/reset-password', '/services', '/customer-preview', '/worker-preview', '/book-ride', '/ride-confirmation', '/booking-confirmation', '/track'];
      const currentPath = window.location.pathname;
      const isPublicPage = publicPaths.some(path => currentPath === path || currentPath.startsWith(path + '/')) || 
                           currentPath.startsWith('/verify/') || 
                           currentPath.startsWith('/offer/') || 
                           currentPath.startsWith('/book/') || 
                           currentPath.startsWith('/customer/book/') || 
                           currentPath.startsWith('/booking-confirmation/') ||
                           currentPath.startsWith('/ride-confirmation/') ||
                           currentPath.includes('preview');
      
      if (!isPublicPage) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token?token=${token}`),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { 
    current_password: currentPassword, 
    new_password: newPassword 
  }),
};

// Services
export const servicesAPI = {
  getAll: (params) => api.get('/services', { params }),
  getAllIncludingInactive: () => api.get('/services', { params: { include_inactive: true } }),
  getOne: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  bulkDelete: (serviceIds) => api.post('/services/bulk-delete', serviceIds),
  search: (query, filters) => api.get('/services', { 
    params: { search: query, ...filters } 
  }),
  seedAll: () => api.post('/services/seed-all'),
  // Toggle service active status
  toggleStatus: (id) => api.patch(`/services/${id}/toggle-status`),
  // Bulk toggle status for multiple services
  bulkToggleStatus: (serviceIds, isActive) => api.post('/services/bulk-toggle-status', serviceIds, { params: { is_active: isActive } }),
  // Get active service types (Single Source of Truth)
  getActiveTypes: () => api.get('/services/types/active'),
};

// Applications
export const applicationsAPI = {
  submit: (data) => api.post('/applications', data),
  getAll: (params) => api.get('/applications', { params }),
  getOne: (id) => api.get(`/applications/${id}`),
  approve: (id) => api.post(`/applications/${id}/approve`),
  reject: (id, reason) => api.post(`/applications/${id}/reject?reason=${encodeURIComponent(reason)}`),
  delete: (id) => api.delete(`/applications/${id}`),
  getRejectionReasons: () => api.get('/applications/rejection-reasons'),
  uploadDocument: (documentType, expirationDate, file) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    if (expirationDate) {
      formData.append('expiration_date', expirationDate);
    }
    formData.append('file', file);
    return api.post('/applications/upload-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Workers
export const workersAPI = {
  getAll: (params) => api.get('/workers', { params }),
  getOne: (id) => api.get(`/workers/${id}`),
  getPublic: (id) => api.get(`/workers/public/${id}`),
  getMyProfile: () => api.get('/workers/me'),
  updateMyProfile: (data) => api.put('/workers/me', data),
  update: (id, data) => api.put(`/workers/${id}`, data),
  createManual: (data) => api.post('/workers/create', data),
  delete: (id) => api.delete(`/workers/${id}`),
  uploadPicture: (formData) => api.post('/workers/me/picture', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  sendOffer: (id, discardPrevious = false) => api.post(`/workers/${id}/send-offer?discard_previous=${discardPrevious}`),
  getWorkerFile: (workerId) => api.get(`/workers/${workerId}/file`),
  // Payment Details
  getPaymentDetails: (workerId) => api.get(`/workers/${workerId}/payment-details`),
  updatePaymentDetails: (workerId, data) => api.put(`/workers/${workerId}/payment-details`, data),
  clearPaymentDetails: (workerId) => api.delete(`/workers/${workerId}/payment-details`),
};

// Bookings
export const bookingsAPI = {
  create: (data) => api.post('/bookings', data),
  createGuest: (data) => api.post('/bookings/guest', data),
  createManual: (data) => api.post('/bookings/manual', data),
  getAll: (params) => api.get('/bookings', { params }),
  getOne: (id) => api.get(`/bookings/${id}`),
  getDetails: (id) => api.get(`/bookings/${id}/details`),
  assignWorker: (bookingId, workerId) => api.post(`/bookings/${bookingId}/assign?worker_id=${workerId}`),
  assignMultipleWorkers: (bookingId, workerIds) => api.post(`/bookings/${bookingId}/assign-multiple`, workerIds),
  removeWorker: (bookingId, workerId) => api.delete(`/bookings/${bookingId}/workers/${workerId}`),
  updateStatus: (id, status) => api.put(`/bookings/${id}/status?status=${status}`),
  manage: (id, data) => api.put(`/bookings/${id}/manage`, data),
  reschedule: (id, data) => api.put(`/bookings/${id}/reschedule`, data),
  delete: (id, reason) => api.delete(`/bookings/${id}`, { params: { reason } }),
  cancel: (id) => api.put(`/bookings/${id}/status?status=cancelled`),
  // Multi-worker management
  updateWorkersNeeded: (bookingId, workersNeeded, minWorkers = 1, maxWorkers = null) => 
    api.put(`/bookings/${bookingId}/workers-needed`, null, { 
      params: { workers_needed: workersNeeded, min_workers: minWorkers, max_workers: maxWorkers || workersNeeded } 
    }),
  getAvailableJobs: () => api.get('/bookings/job-board/available'),
  pickUpJob: (bookingId) => api.post(`/bookings/${bookingId}/pick-up`),
  // Cash payment verification
  verifyCashPayment: (bookingId, data) => api.post(`/bookings/${bookingId}/verify-cash-payment`, data),
  getCashPaymentStatus: (bookingId) => api.get(`/bookings/${bookingId}/cash-payment-status`),
  // Time estimation
  estimateTime: (data) => api.post('/bookings/estimate-time', data),
  overrideTime: (bookingId, data) => api.put(`/bookings/${bookingId}/override-time`, data),
  recordActualTime: (bookingId, minutes) => api.put(`/bookings/${bookingId}/actual-time?actual_minutes=${minutes}`),
  getTimeAccuracyStats: (params) => api.get('/bookings/time-accuracy/stats', { params }),
  // Batch operations
  batchAssignToOwner: (bookingIds, ownerId) => Promise.all(bookingIds.map(id => api.post(`/bookings/${id}/assign?worker_id=${ownerId}`))),
  // Courier-specific operations
  courierVerifyPickup: (bookingId, data) => api.patch(`/bookings/${bookingId}/courier/pickup`, data),
  courierVerifyDelivery: (bookingId, data) => api.patch(`/bookings/${bookingId}/courier/delivery`, data),
  courierUpdateStop: (bookingId, data) => api.patch(`/bookings/${bookingId}/courier/stop`, data),
  courierGetStatus: (bookingId) => api.get(`/bookings/${bookingId}/courier/status`),
};

// Rides
export const ridesAPI = {
  getAll: (params) => api.get('/rides', { params }),
  getOne: (id) => api.get(`/rides/${id}`),
  create: (data) => api.post('/rides', data),
  createManual: (data) => api.post('/rides/manual', data),
  cancel: (id, reason) => api.put(`/rides/${id}/cancel`, null, { params: { reason: reason || '' } }),
  delete: (id) => api.delete(`/rides/${id}`),
  accept: (id) => api.post(`/rides/${id}/accept`),
  start: (id, lat, lng) => api.put(`/rides/${id}/start?driver_lat=${lat}&driver_lng=${lng}`),
  complete: (id, lat, lng) => api.put(`/rides/${id}/complete?driver_lat=${lat}&driver_lng=${lng}`),
  assign: (id, driverId) => api.put(`/rides/${id}/assign?driver_id=${driverId}`),
  updateLocation: (id, lat, lng, heading) => api.put(`/rides/${id}/location`, { lat, lng, heading }),
  getJobBoard: () => api.get('/rides/job-board'),
  getDriverStats: () => api.get('/rides/driver-stats'),
  // Ride pricing configuration (used by owner dashboard)
  getPricing: () => api.get('/rides/pricing'),
  updatePricing: (data) => api.put('/rides/pricing', data),
  estimateFare: (data) => api.post('/rides/estimate-fare', data),
};

// Drivers
export const driversAPI = {
  getMyProfile: () => api.get('/drivers/me'),
  setAvailability: (isAvailable) => api.put(`/drivers/me/availability?is_available=${isAvailable}`),
  getAll: (params) => api.get('/drivers', { params }),
  getOne: (id) => api.get(`/drivers/${id}`),
  getStats: () => api.get('/drivers/stats/overview'),
};

// Pricing Configuration
export const pricingAPI = {
  getConfig: () => api.get('/pricing/config'),
  updateConfig: (data) => api.put('/pricing/config', data),
  updateFuelSettings: (data) => api.put('/pricing/fuel-settings', data),
  updateServicePricing: (serviceType, data) => api.put(`/pricing/service/${serviceType}`, data),
  calculateRide: (data) => api.post('/pricing/calculate/ride', data),
  calculateCleaning: (data) => api.post('/pricing/calculate/cleaning', data),
  calculateCourier: (data) => api.post('/pricing/calculate/courier', data),
  calculateErrands: (data) => api.post('/pricing/calculate/errands', data),
  calculateLaundry: (data) => api.post('/pricing/calculate/laundry', data),
};

// Customer Features
export const customerAPI = {
  // Booking Management
  cancelBooking: (id, reason) => api.put(`/customer/bookings/${id}/cancel`, { reason }),
  exportBookings: (params) => api.get('/customer/bookings/export', { params, responseType: 'blob' }),
  
  // Favorite Workers
  getFavoriteWorkers: () => api.get('/customer/favorites'),
  addFavoriteWorker: (workerId, notes) => api.post('/customer/favorites', { worker_id: workerId, notes }),
  removeFavoriteWorker: (workerId) => api.delete(`/customer/favorites/${workerId}`),
  getFavorites: () => api.get('/customer/favorites'),
  addFavorite: (workerId, notes = null) => api.post('/customer/favorites', { worker_id: workerId, notes }),
  removeFavorite: (workerId) => api.delete(`/customer/favorites/${workerId}`),
  
  // Saved Addresses
  getAddresses: () => api.get('/customer/addresses'),
  addAddress: (data) => api.post('/customer/addresses', data),
  updateAddress: (addressId, data) => api.put(`/customer/addresses/${addressId}`, data),
  deleteAddress: (addressId) => api.delete(`/customer/addresses/${addressId}`),
  
  // Family Members
  getFamily: () => api.get('/customer/family'),
  addFamilyMember: (data) => api.post('/customer/family', data),
  updateFamilyMember: (memberId, data) => api.put(`/customer/family/${memberId}`, data),
  deleteFamilyMember: (memberId) => api.delete(`/customer/family/${memberId}`),
  
  // Quick Rebook
  quickRebook: (data) => api.post('/customer/rebook', data),
  
  // Calendar Export
  exportCalendar: () => api.get('/customer/calendar/export', { responseType: 'blob' }),
  
  // Upcoming Appointment
  getUpcomingAppointment: () => api.get('/customer/upcoming-appointment'),
  
  // Gallery
  getGallery: (limit = 20) => api.get(`/customer/gallery?limit=${limit}`),
  
  // Payment Methods
  getPaymentMethods: () => api.get('/customer/payment-methods'),
  deletePaymentMethod: (methodId) => api.delete(`/customer/payment-methods/${methodId}`),
  
  // Worker Reviews
  getWorkerReviews: (workerId, limit = 10) => api.get(`/customer/worker-reviews/${workerId}?limit=${limit}`),
};

// Analytics & Reports
export const analyticsAPI = {
  getRevenue: (params) => api.get('/analytics/revenue', { params }),
  getWorkerPerformance: (params) => api.get('/analytics/workers/performance', { params }),
  getPopularServices: (params) => api.get('/analytics/services/popular', { params }),
  getCustomerInsights: (params) => api.get('/analytics/customers/insights', { params }),
  getDashboard: () => api.get('/analytics/dashboard'),
  exportRevenue: (params) => api.get('/analytics/export/revenue', { params, responseType: 'blob' }),
  exportWorkers: (params) => api.get('/analytics/export/workers', { params, responseType: 'blob' }),
};

// Worker Availability
export const availabilityAPI = {
  getMy: () => api.get('/availability/my'),
  updateMy: (data) => api.put('/availability/my', data),
  addException: (data) => api.post('/availability/my/exception', data),
  removeException: (date) => api.delete(`/availability/my/exception/${date}`),
  getMyExceptions: (params) => api.get('/availability/my/exceptions', { params }),
  getWorker: (workerId) => api.get(`/availability/worker/${workerId}`),
  getAll: () => api.get('/availability/all'),
  checkAvailability: (workerId, date, startTime, duration) => 
    api.get('/availability/check', { params: { worker_id: workerId, date, start_time: startTime, duration_hours: duration } }),
};

// Service Packages
export const packagesAPI = {
  getAll: (params) => api.get('/packages', { params }),
  getOne: (id) => api.get(`/packages/${id}`),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`),
  book: (id, data) => api.post(`/packages/${id}/book`, null, { params: data }),
};

// Calendar & Scheduling
export const calendarAPI = {
  getBookings: (startDate, endDate, workerId) => 
    api.get('/calendar/bookings', { params: { start_date: startDate, end_date: endDate, worker_id: workerId } }),
  getDaySchedule: (date) => api.get(`/calendar/day/${date}`),
  getWorkerSchedule: (workerId, startDate, endDate) => 
    api.get(`/workers/${workerId}/schedule`, { params: { start_date: startDate, end_date: endDate } }),
  checkWorkerConflicts: (workerId, date, time, duration, excludeBookingId) =>
    api.get(`/workers/${workerId}/conflicts`, { 
      params: { date, time, duration, exclude_booking_id: excludeBookingId } 
    }),
};

// Clock In/Out (Legacy - basic clock action)
export const legacyClockAPI = {
  action: (data) => api.post('/clock', data),
  getTimeEntries: () => api.get('/time-entries'),
};

// Schedules
export const schedulesAPI = {
  getAll: (params) => api.get('/schedules/worker', { params }),
  getAllOwner: (params) => api.get('/schedules/all', { params }),
  getWeeklyHours: (weekStart) => api.get(`/schedules/weekly-hours?week_start=${weekStart}`),
  blockWorkerWeek: (workerId, weekStart) => api.post(`/workers/${workerId}/block-week?week_start=${weekStart}`),
  unblockWorkerWeek: (workerId, weekStart) => api.post(`/workers/${workerId}/unblock-week?week_start=${weekStart}`),
  create: (data) => api.post('/schedules', data),
  getWorkerSchedule: (workerId, startDate, endDate) => 
    api.get('/schedules/worker', { params: { start_date: startDate, end_date: endDate } }),
};

// Payroll
export const payrollAPI = {
  getStubs: () => api.get('/payroll/stubs'),
  generate: (start, end) => api.post(`/payroll/generate?pay_period_start=${start}&pay_period_end=${end}`),
  approveStub: (id) => api.post(`/payroll/stubs/${id}/approve`),
};

// Payments
export const paymentsAPI = {
  createCheckout: (data) => api.post('/payments/checkout', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  checkStatus: (bookingId) => api.get(`/payments/booking/${bookingId}/status`),
  // Payment management (Owner)
  getAll: (params) => api.get('/payments', { params }),
  update: (bookingId, data) => api.put(`/payments/${bookingId}`, data),
  delete: (bookingId) => api.delete(`/payments/${bookingId}`),
  getInvoice: (bookingId) => api.get(`/payments/${bookingId}/invoice`, { responseType: 'blob' }),
  getReceipt: (bookingId) => api.get(`/payments/${bookingId}/receipt`, { responseType: 'blob' }),
  getUrgentBookings: () => api.get('/payments/urgent'),
  getUrgentFeeConfig: () => api.get('/payments/config/urgent-fee'),
  updateUrgentFeeConfig: (percentage) => api.put('/payments/config/urgent-fee', { urgent_fee_percentage: percentage }),
};

// Reviews
export const reviewsAPI = {
  create: (data) => api.post('/reviews', data),
  getAll: (params) => api.get('/reviews', { params }),
  getBookingReview: (bookingId) => api.get(`/reviews/booking/${bookingId}`),
  getWorkerReviews: (workerId) => api.get(`/workers/${workerId}/reviews`),
  // Customer endpoints
  submitReview: (bookingId, rating, comment) => api.post('/reviews/', null, {
    params: { booking_id: bookingId, rating, comment }
  }),
  getMyReviews: () => api.get('/reviews/my-reviews'),
  getBookingsToReview: () => api.get('/reviews/bookings-to-review'),
  // Public endpoints
  getPublic: (featuredOnly = false, serviceType = null, limit = 50) => api.get('/reviews/public', {
    params: { featured_only: featuredOnly, service_type: serviceType, limit }
  }),
  getStats: () => api.get('/reviews/stats'),
  // Owner endpoints
  getPending: () => api.get('/reviews/pending'),
  approve: (reviewId, isFeatured = false) => api.post(`/reviews/${reviewId}/approve`, null, {
    params: { is_featured: isFeatured }
  }),
  reject: (reviewId) => api.post(`/reviews/${reviewId}/reject`),
  delete: (reviewId) => api.delete(`/reviews/${reviewId}`),
  toggleFeatured: (reviewId) => api.post(`/reviews/${reviewId}/feature`),
  archive: (reviewId) => api.post(`/reviews/${reviewId}/archive`),
  notifyWorker: (reviewId) => api.post(`/reviews/${reviewId}/notify-worker`),
};

// File Uploads
export const uploadsAPI = {
  // Worker uploads
  uploadWorkerDocument: (formData) => api.post('/uploads/worker/document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadJobPhoto: (formData) => api.post('/uploads/worker/job-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Customer uploads
  uploadServicePhoto: (formData) => api.post('/uploads/customer/service-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadCustomerDocument: (formData) => api.post('/uploads/customer/document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Get uploads
  getMyUploads: (uploadType) => api.get('/uploads/my-uploads', { params: { upload_type: uploadType } }),
  getBookingUploads: (bookingId) => api.get(`/uploads/booking/${bookingId}`),
  getAllUploads: (params) => api.get('/uploads/all', { params }),
  
  // Owner actions
  approve: (uploadId) => api.post(`/uploads/${uploadId}/approve`),
  reject: (uploadId, reason) => api.post(`/uploads/${uploadId}/reject`, null, { params: { reason } }),
  delete: (uploadId) => api.delete(`/uploads/${uploadId}`),
};

// Mileage
export const mileageAPI = {
  calculate: (customerZip) => api.post('/calculate-mileage', { customer_zip: customerZip }),
  calculateDistance: (fromZip, toZip) => api.post('/calculate-distance', { from_zip: fromZip, to_zip: toZip }),
};

// Recurring Bookings
export const recurringAPI = {
  getSeries: (seriesId) => api.get(`/recurring-bookings/series/${seriesId}`),
  getCustomerSeries: () => api.get('/recurring-bookings/customer'),
  generateNext: (seriesId) => api.post(`/recurring-bookings/${seriesId}/generate-next`),
  pause: (seriesId) => api.put(`/recurring-bookings/${seriesId}/pause`),
  resume: (seriesId) => api.put(`/recurring-bookings/${seriesId}/resume`),
  cancelFuture: (seriesId) => api.delete(`/recurring-bookings/${seriesId}/cancel-future`),
};

// Loyalty Program
export const loyaltyAPI = {
  getAccount: () => api.get('/loyalty/account'),
  getVouchers: () => api.get('/loyalty/vouchers'),
  getHistory: () => api.get('/loyalty/history'),
  redeem: (rewardType) => api.post('/loyalty/redeem', { reward_type: rewardType }),
};

// Enhanced Clock In/Out
export const clockAPI = {
  clockIn: (data) => api.post('/clock/in', data),
  clockOut: (data) => api.post('/clock/out', data),
  getRecords: (params) => api.get('/clock/records', { params }),
  getTimeEntries: () => api.get('/clock/records'),
  getPendingApproval: () => api.get('/clock/pending-approval'),
  approve: (recordId, notes) => api.put(`/clock/approve/${recordId}`, null, { params: { approval_notes: notes } }),
  reject: (recordId, reason) => api.put(`/clock/reject/${recordId}`, null, { params: { rejection_reason: reason } }),
  getRecord: (recordId) => api.get(`/clock/record/${recordId}`),
};

// Payment Reminders
export const paymentRemindersAPI = {
  sendReminders: (reminderType = 'all') => api.post('/payments/send-reminders', null, { params: { reminder_type: reminderType } }),
  getPendingSummary: () => api.get('/payments/pending-summary'),
};

// Waivers
export const waiversAPI = {
  getAll: () => api.get('/waivers'),
};

// Files
export const filesAPI = {
  getUrl: (path) => {
    if (!path) return null;
    // If already a full URL, return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    // Otherwise, construct the full URL with /api/files/ prefix
    return `${API_URL}/api/files/${path}`;
  },
};

// Offer Letters
export const offerLettersAPI = {
  // Public endpoint - no auth needed
  getPublic: (offerId) => api.get(`/offer-letters/public/${offerId}`),
  signPublic: (offerId, agreementsData) => api.post(`/offer-letters/${offerId}/sign-public`, agreementsData),
  // Authenticated endpoints
  getAll: (status) => api.get('/offer-letters', { params: { status } }),
  getOne: (offerId) => api.get(`/offer-letters/${offerId}`),
  sign: (offerId, signatureData) => api.post(`/offer-letters/${offerId}/sign?signature_data=${encodeURIComponent(signatureData)}`),
  reject: (offerId) => api.post(`/offer-letters/${offerId}/reject`),
  resend: (offerId) => api.post(`/offer-letters/${offerId}/resend`),
};

// Worker Payroll & Agreements
export const workerPayrollAPI = {
  getEnrollment: () => api.get('/payroll/enrollment'),
  getAgreements: () => api.get('/worker/agreements'),
  getEarningsSummary: () => api.get('/workers/me/earnings-summary'),
  getPayStubs: () => api.get('/workers/me/pay-stubs'),
};

// Messaging & Chat
export const messagingAPI = {
  // Conversations
  getConversations: (type) => api.get('/conversations', { params: type ? { type } : {} }),
  getConversation: (conversationId) => api.get(`/conversations/${conversationId}`),
  startSupportChat: () => api.post('/conversations/support'),
  startBookingChat: (bookingId) => api.post(`/conversations/booking/${bookingId}`),
  deleteConversation: (conversationId) => api.delete(`/conversations/${conversationId}`),
  updateConversationStatus: (conversationId, status) => api.put(`/conversations/${conversationId}/status?status=${status}`),
  // Messages
  getMessages: (conversationId) => api.get(`/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, content) => api.post('/messages', { conversation_id: conversationId, content }),
  markAsRead: (conversationId) => api.post(`/conversations/${conversationId}/mark-read`),
  getUnreadCount: () => api.get('/messages/unread-count'),
  getMessageSummary: () => api.get('/messages/summary'),
  // File/Image upload in chat
  uploadChatFile: (formData) => api.post('/messages/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Group Chat
  createGroupChat: (data) => api.post('/conversations/group', data),
  addGroupParticipant: (conversationId, participantId) => api.post(`/conversations/group/${conversationId}/add?participant_id=${participantId}`),
  leaveGroupChat: (conversationId) => api.delete(`/conversations/group/${conversationId}/leave`),
  // Owner support
  getSupportConversations: () => api.get('/owner/support-conversations'),
  // Owner booking conversations (worker-customer chats)
  getBookingConversations: () => api.get('/owner/booking-conversations'),
  getConversationMessagesOwner: (conversationId) => api.get(`/owner/conversations/${conversationId}/messages`),
  sendMessageAsOwner: (conversationId, content) => api.post(`/owner/conversations/${conversationId}/message`, { content }),
  // Owner direct messaging
  getAllUsers: () => api.get('/owner/users'),
  startDirectConversation: (recipientId, recipientType, message) => 
    api.post(`/owner/conversations/start?recipient_id=${recipientId}&recipient_type=${recipientType}&initial_message=${encodeURIComponent(message)}`),
  getDirectConversations: () => api.get('/owner/direct-conversations'),
};

// Funds Management
export const fundsAPI = {
  getOverview: () => api.get('/funds/overview'),
  getExpenses: (category, startDate, endDate) => api.get('/funds/expenses', { 
    params: { category, start_date: startDate, end_date: endDate } 
  }),
  createExpense: (category, amount, description, recipient) => 
    api.post(`/funds/expense?category=${category}&amount=${amount}&description=${encodeURIComponent(description)}${recipient ? `&recipient=${encodeURIComponent(recipient)}` : ''}`),
  deleteExpense: (expenseId, reason) => api.delete(`/funds/expense/${expenseId}`, { params: { reason } }),
  deleteTransaction: (transactionId, reason) => api.delete(`/funds/transaction/${transactionId}`, { params: { reason } }),
  getWorkersSummary: () => api.get('/funds/workers-summary'),
};

// Manual Time Entry
export const timeEntryAPI = {
  createManualEntry: (workerId, bookingId, date, clockInTime, clockOutTime, notes) =>
    api.post(`/time/manual-entry?worker_id=${workerId}&booking_id=${bookingId}&date=${date}&clock_in_time=${clockInTime}&clock_out_time=${clockOutTime}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`),
};

// Enhanced Payroll
export const payrollRunAPI = {
  runPayroll: (payPeriodStart, payPeriodEnd) => 
    api.post(`/payroll/run?pay_period_start=${payPeriodStart}&pay_period_end=${payPeriodEnd}`),
  payWorker: (stubId, paymentMethod = 'direct_deposit', notes = '') => 
    api.post(`/payroll/pay/${stubId}?payment_method=${paymentMethod}&notes=${encodeURIComponent(notes)}`),
  getStubs: () => api.get('/payroll/stubs'),
  getPaidStubs: () => api.get('/payroll/stubs/paid'),
  approveStub: (stubId) => api.post(`/payroll/stubs/${stubId}/approve`),
  createManualStub: (data) => api.post('/payroll/manual-stub', data),
  updateStubNotes: (stubId, notes) => api.put(`/payroll/stubs/${stubId}/notes?notes=${encodeURIComponent(notes)}`),
  deleteStub: (stubId) => api.delete(`/payroll/stubs/${stubId}`),
  bulkDeleteStubs: (stubIds) => api.post('/payroll/stubs/bulk-delete', stubIds),
};

// Worker Payment Details
export const workerPaymentAPI = {
  getPaymentDetails: (workerId) => api.get(`/workers/${workerId}/payment-details`),
  updatePaymentDetails: (workerId, data) => api.put(`/workers/${workerId}/payment-details`, data),
};

// Worker Onboarding
export const onboardingAPI = {
  // Worker endpoints
  getWorkerStatus: () => api.get('/onboarding/worker/status'),
  uploadDocument: (formData) => api.post('/onboarding/worker/upload-document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  submitForm: (formType, formData) => api.post(`/onboarding/worker/submit-form?form_type=${formType}`, formData),
  signWaiver: (data) => api.post('/onboarding/worker/sign-waiver', data),
  
  // Owner endpoints
  getTracker: () => api.get('/onboarding/tracker'),
  getWorkerDetails: (workerId) => api.get(`/onboarding/tracker/${workerId}`),
  approveWorker: (workerId, data) => api.post(`/onboarding/tracker/${workerId}/approve`, data),
  sendReminder: (workerId) => api.post(`/onboarding/tracker/${workerId}/send-reminder`),
  getDocument: (workerId, docType) => api.get(`/onboarding/documents/${workerId}/${docType}`),
  ownerUploadDocument: (workerId, formData) => api.post(`/onboarding/tracker/${workerId}/upload-document`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  verifyCompanionRegistry: (workerId, formData) => api.post(`/onboarding/tracker/${workerId}/verify-companion-registry`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  extendDeadline: (workerId, days, reason) => api.post(`/onboarding/tracker/${workerId}/extend-deadline`, null, {
    params: { days, reason }
  }),
  
  // Onboarding Settings (Owner only)
  getSettings: () => api.get('/onboarding-settings'),
  updateSettings: (data) => api.put('/onboarding-settings', data),
  resetSettings: () => api.post('/onboarding-settings/reset'),
  getAvailableForms: () => api.get('/onboarding-settings/available-forms'),
  toggleDocument: (docType, enabled, required) => api.put(`/onboarding-settings/document/${docType}`, null, {
    params: { enabled, required }
  }),
  toggleWaiver: (waiverType, enabled, required) => api.put(`/onboarding-settings/waiver/${waiverType}`, null, {
    params: { enabled, required }
  }),
};

// Owner Email
export const emailAPI = {
  sendEmail: (data) => api.post('/owner/send-email', data),
  getEmailLogs: (limit = 50) => api.get(`/owner/email-logs?limit=${limit}`),
  getRecipients: (type) => api.get(`/owner/email-recipients?recipient_type=${type}`),
};

// Jobs / Job Board
export const jobsAPI = {
  // Worker endpoints
  getAvailable: (params) => api.get('/jobs/available', { params }),
  claimJob: (jobId, data) => api.post(`/jobs/${jobId}/claim`, data || {}),
  withdrawClaim: (jobId) => api.delete(`/jobs/${jobId}/claim`),
  getMyClaims: () => api.get('/jobs/my-claims'),
  getMyAssignments: (params) => api.get('/jobs/my-assignments', { params }),
  getWeeklyHours: () => api.get('/jobs/weekly-hours'),
  
  // Worker assignment response (accept/decline)
  respondToAssignment: (jobId, accept, declineReason) => api.post(`/jobs/${jobId}/respond`, {
    accept,
    decline_reason: declineReason
  }),
  getJobDetails: (jobId) => api.get(`/jobs/${jobId}/details`),
  
  // Owner endpoints
  getJobBoard: (params) => api.get('/jobs/board', { params }),
  getPendingApprovals: () => api.get('/jobs/pending-approval'),
  approveJob: (jobId, data) => api.post(`/jobs/${jobId}/approve`, data),
  assignJob: (jobId, data) => api.post(`/jobs/${jobId}/assign`, data),
  unassignJob: (jobId, reason) => api.post(`/jobs/${jobId}/unassign`, { reason }),
  postJob: (jobId) => api.post(`/jobs/${jobId}/post`),
  postAllPending: () => api.post('/jobs/post-all-pending'),
  
  // Declined jobs (Owner)
  getDeclinedJobs: () => api.get('/jobs/declined'),
  getDeclineHistory: (jobId) => api.get(`/jobs/${jobId}/decline-history`),
  
  // Worker hours management
  getWorkerWeeklyHours: (workerId) => api.get(`/jobs/workers/${workerId}/weekly-hours`),
  updateWorkerWeeklyCap: (workerId, data) => api.put(`/jobs/workers/${workerId}/weekly-cap`, data),
  getAllWorkersHours: () => api.get('/jobs/all-workers-hours'),
  
  // Worker profile summary
  getWorkerProfileSummary: (workerId) => api.get(`/jobs/workers/${workerId}/profile-summary`),
  
  // Job management
  deleteJob: (jobId) => api.delete(`/jobs/${jobId}`),
  updateJob: (jobId, data) => api.put(`/jobs/${jobId}`, data),
  bulkDeleteJobs: (jobIds, reason) => api.post('/jobs/bulk-delete', jobIds, { params: { reason } }),
};

// Earnings API - Worker earnings management
export const earningsAPI = {
  getWorkerEarnings: () => api.get('/workers/me/earnings-summary'),
  getPaymentHistory: (params) => api.get('/workers/me/payment-history', { params }),
};

// Archive API
export const archiveAPI = {
  // Get archive summary
  getSummary: () => api.get('/archive/summary'),
  
  // Get deleted items
  getDeletedWorkers: (page = 1) => api.get('/archive/workers', { params: { page } }),
  getDeletedJobs: (page = 1) => api.get('/archive/jobs', { params: { page } }),
  getCompletedJobs: (page = 1) => api.get('/archive/completed-jobs', { params: { page } }),
  getCancelledBookings: (page = 1) => api.get('/archive/cancelled-bookings', { params: { page } }),
  getDeletedTransactions: (page = 1) => api.get('/archive/transactions', { params: { page } }),
  getDeletedApplications: (page = 1) => api.get('/archive/applications', { params: { page } }),
  getArchivedConversations: (page = 1) => api.get('/archive/conversations', { params: { page } }),
  getArchivedPaystubs: (page = 1) => api.get('/archive/paystubs', { params: { page } }),
  
  // Restore items
  restoreWorker: (workerId, reason) => api.post(`/archive/workers/${workerId}/restore`, { reason }),
  restoreJob: (jobId, reason) => api.post(`/archive/jobs/${jobId}/restore`, { reason }),
  restoreTransaction: (transactionId, reason) => api.post(`/archive/transactions/${transactionId}/restore`, { reason }),
  restoreApplication: (appId, reason) => api.post(`/archive/applications/${appId}/restore`, { reason }),
  
  // Permanent delete
  permanentDeleteWorker: (workerId) => api.delete(`/archive/workers/${workerId}/permanent`),
  permanentDeleteJob: (jobId) => api.delete(`/archive/jobs/${jobId}/permanent`),
  permanentDeleteTransaction: (transactionId) => api.delete(`/archive/transactions/${transactionId}/permanent`),
  permanentDeleteApplication: (appId) => api.delete(`/archive/applications/${appId}/permanent`),
  permanentDeleteConversation: (convId) => api.delete(`/archive/conversations/${convId}/permanent`),
  
  // Archive cancelled booking
  archiveCancelledBooking: (bookingId) => api.post(`/archive/cancelled-bookings/${bookingId}/archive`),
  
  // Email archive
  getArchivedEmails: (page = 1) => api.get('/archive/emails', { params: { page } }),
  bulkDeleteEmails: (emailIds) => api.post('/archive/emails/bulk-delete', emailIds),
  bulkRestoreEmails: (emailIds) => api.post('/archive/emails/bulk-restore', emailIds),
  bulkPermanentDeleteEmails: (emailIds) => api.delete('/archive/emails/bulk-permanent', { data: emailIds }),
  
  // ID Cards archive
  getArchivedIDCards: (skip = 0, limit = 50) => api.get('/archive/id-cards', { params: { skip, limit } }),
  restoreIDCard: (cardId) => api.put(`/archive/id-cards/${cardId}/restore`),
  permanentDeleteIDCard: (cardId) => api.delete(`/archive/id-cards/${cardId}/permanent`),
};

// Notes API (Owner only)
export const notesAPI = {
  // Get all notes across all entities (for Notes Summary Tab)
  getAll: () => api.get('/notes'),
  
  // Get notes for an entity
  getNotes: (entityType, entityId) => api.get(`/notes/${entityType}/${entityId}`),
  
  // Create a note
  create: (data) => api.post('/notes', data),
  
  // Update a note
  update: (noteId, content) => api.put(`/notes/${noteId}`, { content }),
  
  // Delete a note
  delete: (noteId) => api.delete(`/notes/${noteId}`),
  
  // Get note count for badge display
  getCount: (entityType, entityId) => api.get(`/notes/count/${entityType}/${entityId}`),
  
  // Get bulk counts for multiple entities
  getBulkCounts: (entityType, entityIds) => api.get(`/notes/bulk-counts/${entityType}`, { 
    params: { entity_ids: entityIds.join(',') } 
  }),
};

// Completion Forms API (Owner only)
export const completionFormsAPI = {
  // Get all completion forms with filters
  getAll: (params) => api.get('/completion-forms', { params }),
  
  // Get single completion form
  getOne: (formId) => api.get(`/completion-forms/${formId}`),
  
  // Get completion form by booking
  getByBooking: (bookingId) => api.get(`/completion-forms/booking/${bookingId}`),
  
  // Get checklist for a booking (worker use)
  getChecklist: (bookingId) => api.get(`/completion-forms/checklist/${bookingId}`),
  
  // Submit completion form (worker use)
  submit: (data) => api.post('/completion-forms', data),
  
  // Upload photo
  uploadPhoto: (formData) => api.post('/completion-forms/upload-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Download form as PDF
  downloadPDF: (formId) => api.get(`/completion-forms/${formId}/pdf`, { responseType: 'blob' }),
  
  // Delete completion form (owner only)
  delete: (formId) => api.delete(`/completion-forms/${formId}`),
};


// Job Workflow API
export const jobWorkflowAPI = {
  // Clock in for a job
  clockIn: (data) => api.post('/job-workflow/clock-in', data),
  
  // Complete a job
  complete: (data) => api.post('/job-workflow/complete', data),
  
  // Upload job photo (before/after)
  uploadPhoto: (formData) => api.post('/job-workflow/photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Get photos for a booking
  getPhotos: (bookingId, photoType = null) => api.get(`/job-workflow/photos/${bookingId}`, { 
    params: photoType ? { photo_type: photoType } : {} 
  }),
  
  // Delete a photo
  deletePhoto: (photoId) => api.delete(`/job-workflow/photos/${photoId}`),
  
  // Get booking details for workflow
  getBookingDetails: (bookingId) => api.get(`/job-workflow/booking/${bookingId}/details`),
  
  // Get job completions history
  getCompletions: (params) => api.get('/job-workflow/completions', { params }),
  
  // Download signature PDF
  downloadSignaturePDF: (bookingId) => api.get(`/job-workflow/signature/${bookingId}/pdf`, { responseType: 'blob' }),
};


// Notifications API
export const notificationsAPI = {
  getAll: (unreadOnly = false, limit = 50) => api.get('/notifications/', { params: { unread_only: unreadOnly, limit } }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
  clearAll: () => api.delete('/notifications/'),
};


// Coupons API
export const couponsAPI = {
  // Owner endpoints
  getAll: (activeOnly = false) => api.get('/coupons/', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/coupons/', data),
  update: (couponId, data) => api.put(`/coupons/${couponId}`, data),
  delete: (couponId) => api.delete(`/coupons/${couponId}`),
  
  // Customer endpoints
  validate: (code, orderAmount, serviceType = null) => api.post('/coupons/validate', { 
    code, 
    order_amount: orderAmount,
    service_type: serviceType 
  }),
  apply: (code, bookingId) => api.post('/coupons/apply', null, { 
    params: { code, booking_id: bookingId } 
  }),
  checkFirstTime: () => api.get('/coupons/check-first-time'),
};


// Gallery API (Before/After Photos)
export const galleryAPI = {
  // Worker endpoints
  uploadPhoto: (formData) => api.post('/gallery/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Owner endpoints
  getPending: () => api.get('/gallery/pending'),
  approvePhoto: (photoId, isFeatured = false) => api.post(`/gallery/${photoId}/approve`, null, {
    params: { is_featured: isFeatured }
  }),
  rejectPhoto: (photoId) => api.post(`/gallery/${photoId}/reject`),
  toggleFeatured: (photoId) => api.post(`/gallery/${photoId}/feature`),
  deletePhoto: (photoId) => api.delete(`/gallery/${photoId}`),
  
  // Public endpoints
  getApproved: (featuredOnly = false, limit = 50) => api.get('/gallery/approved', {
    params: { featured_only: featuredOnly, limit }
  }),
  getPairs: (approvedOnly = true, featuredOnly = false, limit = 20) => api.get('/gallery/pairs', {
    params: { approved_only: approvedOnly, featured_only: featuredOnly, limit }
  }),
};


// Referral Program API
export const referralsAPI = {
  // Customer endpoints
  getMyCode: () => api.get('/referrals/my-code'),
  getStats: () => api.get('/referrals/stats'),
  getMyReferrals: () => api.get('/referrals/my-referrals'),
  applyCode: (code) => api.post('/referrals/apply', { referral_code: code }),
  checkDiscount: () => api.get('/referrals/check-discount'),
  
  // Credit endpoints
  getCreditBalance: () => api.get('/referrals/credits/balance'),
  applyCredits: (bookingId, amount) => api.post('/referrals/credits/apply', null, {
    params: { booking_id: bookingId, amount }
  }),
  
  // Public endpoint
  validateCode: (code) => api.post('/referrals/validate', null, { params: { code } }),
  
  // Owner endpoint
  creditReferral: (referralId) => api.post(`/referrals/credit/${referralId}`),
};


// Education & Training API
export const educationAPI = {
  // Owner endpoints
  getCourses: (category = null, activeOnly = true) => api.get('/education/courses', {
    params: { category, active_only: activeOnly }
  }),
  createCourse: (data) => api.post('/education/courses', data),
  updateCourse: (courseId, data) => api.put(`/education/courses/${courseId}`, data),
  deleteCourse: (courseId) => api.delete(`/education/courses/${courseId}`),
  assignCourse: (workerId, courseId, dueDate = null) => api.post('/education/assign', {
    worker_id: workerId,
    course_id: courseId,
    due_date: dueDate
  }),
  assignBulk: (courseId, workerIds = null, allWorkers = false, serviceType = null) => api.post('/education/assign-bulk', null, {
    params: { course_id: courseId, worker_ids: workerIds, all_workers: allWorkers, service_type: serviceType }
  }),
  getAllProgress: (courseId = null, workerId = null, status = null) => api.get('/education/progress/all', {
    params: { course_id: courseId, worker_id: workerId, status }
  }),
  getOverviewStats: () => api.get('/education/stats/overview'),
  getWorkerStats: (workerId) => api.get(`/education/workers/${workerId}/stats`),
  
  // Worker endpoints
  getMyCourses: (status = null) => api.get('/education/my-courses', { params: { status } }),
  getMyStats: () => api.get('/education/my-stats'),
  updateProgress: (courseId, progressPercent, completed = false, quizScore = null) => api.post('/education/progress', {
    course_id: courseId,
    progress_percent: progressPercent,
    completed,
    quiz_score: quizScore
  }),
  getCourseDetails: (courseId) => api.get(`/education/course/${courseId}`),
};

// Policies API
export const policiesAPI = {
  // Owner endpoints
  create: (policyData) => api.post('/policies', policyData),
  getAll: (category = null, activeOnly = true) => api.get('/policies', { params: { category, active_only: activeOnly } }),
  getOne: (policyId) => api.get(`/policies/${policyId}`),
  update: (policyId, updateData) => api.put(`/policies/${policyId}`, updateData),
  delete: (policyId) => api.delete(`/policies/${policyId}`),
  
  // Signature endpoints (owner)
  getSignatures: (policyId) => api.get(`/policies/signatures/${policyId}`),
  getSigningStats: () => api.get('/policies/stats/signing'),
  getRequiredStats: () => api.get('/policies/stats/required'),
  sendReminder: (policyId, workerId = null) => api.post(`/policies/signatures/${policyId}/remind`, null, { params: workerId ? { worker_id: workerId } : {} }),
  sendReminderAll: (policyId) => api.post(`/policies/signatures/${policyId}/remind-all`),
  
  // Worker endpoints
  sign: (policyId) => api.post('/policies/sign', { policy_id: policyId, agreed: true }),
  getMySignatures: () => api.get('/policies/signatures/my'),
  
  // Legacy endpoints (backwards compatibility)
  acknowledge: (policyId) => api.post('/policies/acknowledge', { policy_id: policyId, agreed: true }),
  getMyAcknowledgements: () => api.get('/policies/acknowledgements/my'),
  getAcknowledgements: (policyId) => api.get(`/policies/acknowledgements/${policyId}`),
};

// Evaluations/Discipline API
export const evaluationsAPI = {
  // Owner endpoints
  create: (evaluationData) => api.post('/evaluations', evaluationData),
  getAll: (filters = {}) => api.get('/evaluations', { params: filters }),
  getOne: (evaluationId) => api.get(`/evaluations/${evaluationId}`),
  update: (evaluationId, updateData) => api.put(`/evaluations/${evaluationId}`, updateData),
  delete: (evaluationId) => api.delete(`/evaluations/${evaluationId}`),
  getStats: () => api.get('/evaluations/stats'),
  sendReminder: (evaluationId) => api.post(`/evaluations/${evaluationId}/remind`),
  
  // Worker endpoints
  getMyEvaluations: (status = null) => api.get('/evaluations/worker/my', { params: status ? { status } : {} }),
  sign: (evaluationId, workerResponse = null) => api.post(`/evaluations/${evaluationId}/sign`, { worker_response: workerResponse }),
};

// Document Review API
export const documentsAPI = {
  // Owner endpoints
  reviewDocument: (source, sourceId, docType, action, reason = null, notes = null) => 
    api.post(`/documents/review/${source}/${sourceId}/${docType}`, { action, reason, notes }),
  getPendingDocuments: () => api.get('/documents/pending'),
  getStats: () => api.get('/documents/stats'),
  sendFeedbackEmail: (workerId, subject = null, includeChecklist = true, additionalNotes = null) =>
    api.post('/documents/feedback-email', { worker_id: workerId, subject, include_checklist: includeChecklist, additional_notes: additionalNotes }),
  
  // Worker/Owner profile completion
  getProfileCompletion: (workerId) => api.get(`/documents/worker/${workerId}/profile-completion`),
};

// Assignment Email API
export const assignmentEmailsAPI = {
  getPreview: (bookingId, workerId) => api.post('/assignment-emails/preview', { booking_id: bookingId, worker_id: workerId }),
  sendEmail: (bookingId, workerId, subject, htmlContent, sendCopyToOwner = true) => 
    api.post('/assignment-emails/send', { booking_id: bookingId, worker_id: workerId, subject, html_content: htmlContent, send_copy_to_owner: sendCopyToOwner }),
  getPendingEmails: () => api.get('/assignment-emails/pending'),
  getFailedEmails: (limit = 50) => api.get(`/assignment-emails/failed?limit=${limit}`),
  retryFailedEmail: (emailId) => api.post(`/assignment-emails/failed/${emailId}/retry`),
  dismissFailedEmail: (emailId) => api.delete(`/assignment-emails/failed/${emailId}`),
};

// SMS API
export const smsAPI = {
  getConfigStatus: () => api.get('/sms/config-status'),
  getStats: () => api.get('/sms/stats'),
  getLogs: (limit = 100, status = null) => api.get(`/sms/logs?limit=${limit}${status ? `&status=${status}` : ''}`),
  sendTest: (phoneNumber, message) => api.post('/sms/send-test', { phone_number: phoneNumber, message }),
  deleteLog: (logId) => api.delete(`/sms/logs/${logId}`),
};

// Receipts API
export const receiptsAPI = {
  upload: (data) => api.post('/receipts', data),
  getAll: (params) => api.get('/receipts', { params }),
  getStats: () => api.get('/receipts/stats'),
  getOne: (id) => api.get(`/receipts/${id}`),
  getByBooking: (bookingId) => api.get(`/receipts/booking/${bookingId}`),
  process: (id, data) => api.put(`/receipts/${id}/process`, data),
  getArchived: () => api.get('/receipts/archived'),
  restore: (id) => api.put(`/receipts/${id}/restore`),
};

// Tips API
export const tipsAPI = {
  create: (data) => api.post('/tips', data),
  getAll: (params) => api.get('/tips', { params }),
  getStats: () => api.get('/tips/stats'),
  getSettings: () => api.get('/tips/settings'),
  updateSettings: (data) => api.put('/tips/settings', data),
  approve: (id) => api.put(`/tips/${id}/approve`),
  markPaid: (id) => api.put(`/tips/${id}/paid`),
  getWorkerTips: (workerId) => api.get(`/tips/worker/${workerId}`),
  getArchived: () => api.get('/tips/archived'),
  restore: (id) => api.put(`/tips/${id}/restore`),
};

// Courier API
export const courierAPI = {
  // Jobs
  createJob: (data) => api.post('/courier/jobs', data),
  getJobs: (params) => api.get('/courier/jobs', { params }),
  getJob: (id) => api.get(`/courier/jobs/${id}`),
  updateJob: (id, data) => api.put(`/courier/jobs/${id}`, data),
  assignJob: (id, workerId) => api.put(`/courier/jobs/${id}/assign?worker_id=${workerId}`),
  claimJob: (id) => api.put(`/courier/jobs/${id}/claim`),
  markPickup: (id, data) => api.put(`/courier/jobs/${id}/pickup`, null, { params: data }),
  markDelivered: (id, data) => api.put(`/courier/jobs/${id}/deliver`, null, { params: data }),
  completeJob: (id, amount) => api.put(`/courier/jobs/${id}/complete`, null, { params: { final_amount: amount } }),
  archiveJob: (id) => api.delete(`/courier/jobs/${id}`),
  restoreJob: (id) => api.put(`/courier/jobs/${id}/restore`),
  getArchivedJobs: () => api.get('/courier/jobs/archived'),
  getStats: () => api.get('/courier/jobs/stats'),
  
  // Medical approval
  approveJob: (id) => api.put(`/courier/jobs/${id}/approve`),
  rejectJob: (id, reason) => api.put(`/courier/jobs/${id}/reject?reason=${encodeURIComponent(reason)}`),
  
  // Pricing
  getPricing: () => api.get('/courier/settings/pricing'),
  updatePricing: (data) => api.put('/courier/settings/pricing', data),
  
  // Worker certifications
  getCertifications: () => api.get('/courier/workers/certifications'),
  getCertification: (workerId) => api.get(`/courier/workers/${workerId}/certification`),
  updateCertification: (workerId, data) => api.put(`/courier/workers/${workerId}/certification`, data),
  signAgreement: (workerId) => api.post(`/courier/workers/${workerId}/sign-agreement`),
  completeTraining: (workerId) => api.post(`/courier/workers/${workerId}/complete-training`),
  
  // Logs & Mileage
  getLogs: (params) => api.get('/courier/logs', { params }),
  calculateMileage: (params) => api.post('/courier/calculate-mileage', null, { params }),
};

// ID Cards API
export const idCardsAPI = {
  // Get all ID cards (owner)
  getAll: (status) => api.get('/id-cards', { params: status ? { status } : {} }),
  
  // Get stats
  getStats: () => api.get('/id-cards/stats'),
  
  // Get worker's ID card
  getWorkerCard: (workerId) => api.get(`/id-cards/worker/${workerId}`),
  
  // Worker gets own card
  getMyCard: () => api.get('/id-cards/my-card'),
  
  // Worker requests card
  requestCard: () => api.post('/id-cards/request'),
  
  // Owner issues card to worker
  issueCard: (workerId, notes) => api.post(`/id-cards/issue/${workerId}`, notes ? { notes } : null),
  
  // Approve pending card
  approveCard: (cardId) => api.put(`/id-cards/${cardId}/approve`),
  
  // Revoke card
  revokeCard: (cardId, reason) => api.put(`/id-cards/${cardId}/revoke`, null, { params: reason ? { reason } : {} }),
  
  // Update card
  updateCard: (cardId, data) => api.put(`/id-cards/${cardId}`, data),
  
  // Delete card
  deleteCard: (cardId) => api.delete(`/id-cards/${cardId}`),
  
  // Verify card (public)
  verifyCard: (cardId) => api.get(`/id-cards/verify/${cardId}`),
};

// Service Add-Ons API
export const addonsAPI = {
  // Get all add-ons
  getAll: (category, activeOnly) => api.get('/addons', { params: { category, active_only: activeOnly } }),
  
  // Get categories with counts
  getCategories: () => api.get('/addons/categories'),
  
  // Get add-ons for a specific service type
  getForService: (serviceType) => api.get(`/addons/for-service/${serviceType}`),
  
  // Get popular add-ons for a service type
  getPopular: (serviceType, limit = 5) => api.get(`/addons/popular/${serviceType}`, { params: { limit } }),
  
  // Get stats
  getStats: () => api.get('/addons/stats'),
  
  // Create add-on
  create: (data) => api.post('/addons', data),
  
  // Update add-on
  update: (addonId, data) => api.put(`/addons/${addonId}`, data),
  
  // Delete add-on
  delete: (addonId) => api.delete(`/addons/${addonId}`),
  
  // Toggle active status
  toggle: (addonId) => api.put(`/addons/${addonId}/toggle`),
  
  // Seed defaults
  seedDefaults: () => api.post('/addons/seed-defaults'),
};


// Incidents API
export const incidentsAPI = {
  create: (data) => api.post('/incidents', data),
  getAll: (params = {}) => api.get('/incidents', { params }),
  getStats: () => api.get('/incidents/stats'),
  get: (incidentId) => api.get(`/incidents/${incidentId}`),
  updateStatus: (incidentId, data) => api.put(`/incidents/${incidentId}/status`, data),
  edit: (incidentId, data) => api.put(`/incidents/${incidentId}`, data),
  addNote: (incidentId, data) => api.post(`/incidents/${incidentId}/notes`, data),
  getByBooking: (bookingId) => api.get(`/incidents/booking/${bookingId}`),
};

// Customer Policies API
export const customerPoliciesAPI = {
  // Owner endpoints
  create: (data) => api.post('/customer-policies', data),
  getAll: (params = {}) => api.get('/customer-policies', { params }),
  update: (policyId, data) => api.put(`/customer-policies/${policyId}`, data),
  delete: (policyId) => api.delete(`/customer-policies/${policyId}`),
  getSignatures: (policyId = null) => api.get('/customer-policies/signatures', { params: { policy_id: policyId } }),
  seedDefaults: () => api.get('/customer-policies/seed-defaults'),
  
  // Customer endpoints
  getMyRequired: () => api.get('/customer-policies/my-required'),
  sign: (policyId) => api.post(`/customer-policies/${policyId}/sign`, { acknowledged: true }),
};

// Contact Form API
export const contactAPI = {
  submit: (data) => api.post('/contact/submit', data),
  getSubmissions: (status = null) => api.get('/contact/submissions', { params: { status } }),
  updateStatus: (submissionId, status, notes = '') => api.put(`/contact/submissions/${submissionId}/status`, null, { params: { status, notes } }),
};

// Support Center API
export const supportAPI = {
  // Dashboard
  getDashboard: () => api.get('/support/dashboard'),
  
  // Presence
  updatePresence: (status) => api.post('/support/presence', { status }),
  getPresence: (userId) => api.get(`/support/presence/${userId}`),
  getAllPresence: () => api.get('/support/presence'),
  getOwnerStatus: () => api.get('/support/owner-status'),
  
  // Tickets
  createTicket: (data) => api.post('/support/tickets', data),
  getTickets: (params) => api.get('/support/tickets', { params }),
  getTicket: (ticketId) => api.get(`/support/tickets/${ticketId}`),
  updateTicket: (ticketId, data) => api.put(`/support/tickets/${ticketId}`, data),
  respondToTicket: (ticketId, response) => api.post(`/support/tickets/${ticketId}/respond?response=${encodeURIComponent(response)}`),
  
  // Voicemails
  getVoicemails: (direction = 'received', listened = null) => api.get('/support/voicemails', { params: { direction, listened } }),
  createVoicemail: (formData) => api.post('/support/voicemails', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  markVoicemailListened: (voicemailId) => api.post(`/support/voicemails/${voicemailId}/listened`),
  deleteVoicemail: (voicemailId) => api.delete(`/support/voicemails/${voicemailId}`),
  
  // Call Logs
  getCallLogs: (params) => api.get('/support/call-logs', { params }),
  
  // Settings
  getSettings: () => api.get('/support/settings'),
  updateSettings: (settings) => api.put('/support/settings', settings),
};

// Calls/WebRTC API
export const callsAPI = {
  // Contacts
  getContacts: () => api.get('/calls/contacts'),
  
  // Call management
  initiateCall: (recipientId, callType = 'video', conversationId = null) => 
    api.post('/calls/initiate', { recipient_id: recipientId, call_type: callType, conversation_id: conversationId }),
  respondToCall: (callId, action) => api.post('/calls/respond', { call_id: callId, action }),
  endCall: (callId, reason = null) => api.post('/calls/end', { call_id: callId, reason }),
  
  // Call history
  getCallHistory: (limit = 50, callType = null) => api.get('/calls/history', { params: { limit, call_type: callType } }),
  getCallDetail: (callId) => api.get(`/calls/history/${callId}`),
  
  // Recording (owner only)
  toggleRecording: (callId, action) => api.post('/calls/recording', { call_id: callId, action }),
  
  // Support
  getSupportStatus: () => api.get('/calls/support/status'),
  requestSupport: (requestType = 'chat', issue = null) => api.post('/calls/support/request', null, { params: { request_type: requestType, issue } }),
};

// Email Templates API
export const emailTemplatesAPI = {
  // Get template types and variables
  getTypes: () => api.get('/email-templates/types'),
  getVariables: (templateType) => api.get(`/email-templates/variables/${templateType}`),
  getDefaults: () => api.get('/email-templates/defaults'),
  
  // CRUD operations
  list: (params = {}) => api.get('/email-templates', { params }),
  get: (templateId) => api.get(`/email-templates/${templateId}`),
  getByType: (templateType) => api.get(`/email-templates/by-type/${templateType}`),
  create: (template) => api.post('/email-templates', template),
  update: (templateId, updates) => api.put(`/email-templates/${templateId}`, updates),
  delete: (templateId) => api.delete(`/email-templates/${templateId}`),
  
  // Actions
  duplicate: (templateId) => api.post(`/email-templates/${templateId}/duplicate`),
  preview: (data) => api.post('/email-templates/preview', data),
  resetToDefault: (templateType) => api.post(`/email-templates/reset-to-default/${templateType}`),
};

// Communication Center API
export const communicationAPI = {
  // Email Composition
  getRecipients: (recipientType, search = '') => 
    api.get('/communication/email/recipients', { params: { recipient_type: recipientType, search } }),
  getEmailTemplates: () => api.get('/communication/email/templates'),
  sendEmail: (data) => api.post('/communication/email/send', data),
  getDrafts: () => api.get('/communication/email/drafts'),
  deleteDraft: (draftId) => api.delete(`/communication/email/drafts/${draftId}`),
  uploadAttachment: (formData) => api.post('/communication/email/upload-attachment', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // SMS
  getSMSContact: (userId) => api.get(`/communication/sms/contact/${userId}`),
  logSMSAction: (data) => api.post('/communication/sms/log-action', data),
  
  // Communication Logs
  getLogs: (params = {}) => api.get('/communication/logs', { params }),
  getUserHistory: (userId) => api.get(`/communication/logs/by-user/${userId}`),
  getReferenceHistory: (refType, refId) => api.get(`/communication/logs/by-reference/${refType}/${refId}`),
  getStats: () => api.get('/communication/stats'),
  
  // Quick Reply Templates
  getQuickReplies: (category = null) => api.get('/communication/quick-replies', { params: { category } }),
  createQuickReply: (data) => api.post('/communication/quick-replies', data),
  updateQuickReply: (id, data) => api.put(`/communication/quick-replies/${id}`, data),
  deleteQuickReply: (id) => api.delete(`/communication/quick-replies/${id}`),
  useQuickReply: (id) => api.post(`/communication/quick-replies/${id}/use`),
  searchQuickReplyByShortcut: (shortcut) => api.get('/communication/quick-replies/search', { params: { shortcut } }),
};


export default api;
